/**
 * Upload/alteração/remoção de foto de perfil do médico (Configurações).
 */
import { useRef, useState } from 'react';
import { Camera, Loader2, Trash2, User } from 'lucide-react';
import { toast } from 'sonner';
import type { DoctorDTO } from '@/services/doctors';
import { deleteDoctorAvatar, getDoctorAvatarSrc, uploadDoctorAvatar } from '@/services/doctors';
import { Button } from '@/components/ui/button';

interface DoctorAvatarSettingsProps {
  doctor: DoctorDTO;
  onUpdate: (doctor: DoctorDTO) => void;
}

const ACCEPTED = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';
const MAX_BYTES = 5 * 1024 * 1024;

export function DoctorAvatarSettings({ doctor, onUpdate }: DoctorAvatarSettingsProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imgError, setImgError] = useState(false);

  const remoteSrc = getDoctorAvatarSrc(doctor);
  const displaySrc = previewUrl || (!imgError ? remoteSrc : null);
  const initials = (doctor.user?.name || 'M').charAt(0).toUpperCase();

  const handleSelectClick = () => {
    inputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!/\.(jpe?g|png|webp)$/i.test(file.name) && !/^image\/(jpeg|png|webp)$/.test(file.type)) {
      toast.error('Formato inválido. Use JPG, PNG ou WEBP.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('A foto deve ter no máximo 5MB.');
      return;
    }

    const localPreview = URL.createObjectURL(file);
    setPreviewUrl(localPreview);
    setImgError(false);
    setIsUploading(true);

    try {
      const response = await uploadDoctorAvatar(doctor.id, file);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Falha ao enviar foto');
      }
      onUpdate(response.data);
      toast.success('Foto de perfil atualizada.');
      setPreviewUrl(null);
      setImgError(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao enviar foto de perfil.');
      setPreviewUrl(null);
    } finally {
      URL.revokeObjectURL(localPreview);
      setIsUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!doctor.hasAvatar && !remoteSrc) {
      toast.info('Nenhuma foto para remover.');
      return;
    }

    setIsRemoving(true);
    try {
      const response = await deleteDoctorAvatar(doctor.id);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Falha ao remover foto');
      }
      onUpdate(response.data);
      setPreviewUrl(null);
      setImgError(false);
      toast.success('Foto de perfil removida.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao remover foto.');
    } finally {
      setIsRemoving(false);
    }
  };

  return (
    <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-gray-200 dark:border-neutral-800 overflow-hidden">
      <div className="p-6 border-b border-gray-100 dark:border-neutral-800">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-50 dark:bg-teal-900/20 rounded-xl text-teal-600">
            <Camera className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-900 dark:text-white">Foto de perfil</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              JPG, PNG ou WEBP até 5MB. Visível nas buscas e no seu perfil público.
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 flex flex-col sm:flex-row items-start sm:items-center gap-6">
        <div className="relative shrink-0">
          {displaySrc ? (
            <img
              src={displaySrc}
              alt="Foto de perfil"
              className="w-24 h-24 rounded-2xl object-cover border border-gray-200 dark:border-neutral-700"
              onError={() => {
                if (!previewUrl) setImgError(true);
              }}
            />
          ) : (
            <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center text-white text-3xl font-bold shadow-md">
              {initials || <User className="w-10 h-10" />}
            </div>
          )}
          {(isUploading || isRemoving) && (
            <div className="absolute inset-0 rounded-2xl bg-black/40 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-white animate-spin" />
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED}
            className="hidden"
            onChange={handleFileChange}
          />
          <Button
            type="button"
            onClick={handleSelectClick}
            disabled={isUploading || isRemoving}
            className="bg-teal-600 hover:bg-teal-700 text-white"
          >
            {isUploading ? 'Enviando...' : doctor.hasAvatar || remoteSrc ? 'Alterar foto' : 'Enviar foto'}
          </Button>
          {(doctor.hasAvatar || remoteSrc) && (
            <Button
              type="button"
              variant="outline"
              onClick={handleRemove}
              disabled={isUploading || isRemoving}
              className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
              {isRemoving ? 'Removendo...' : 'Remover'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
